import React from 'react';
import { PageHero } from '../components/PageHero';
import { Button } from '../components/ui/Button';
import { navigate, BOOKING_URL } from '../lib/navigation';
import { CheckCircle2 } from 'lucide-react';
export function About() {
  return <div className="animate-fade-in-up" data-id="element-200">
      <PageHero title="About" subtitle="Get to know the person behind the practice." data-id="element-201" />

      <section className="py-20 bg-white" data-id="element-202">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
          <div className="max-w-none" data-id="element-204">
            {/* Intro with headshot */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16 items-start" data-id="element-205">
              <div className="md:col-span-2" data-id="element-206">
                <h2 className="text-3xl font-medium text-teal mb-6" data-id="element-207">
                  Hey, I'm Jess :)
                </h2>
                <p className="text-lg text-teal/80 leading-relaxed mb-6" data-id="element-208">
                  I know what it's like to feel like you're supposed to have it
                  all together while everything feels like it's falling apart.
                  To wonder if everyone else got a manual on adulting that you
                  somehow missed. To be exhausted from trying to be what
                  everyone else needs you to be.
                </p>
                <p className="text-lg text-teal/80 leading-relaxed" data-id="element-209">
                  That's why I became a therapist. Not to fix people, because
                  you're not broken, but to create a space where you can show up
                  as you are and figure out where you want to go.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-210">
                <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-sage/20" data-id="element-211">
                  <img src="https://cdn.magicpatterns.com/uploads/hWXcPz9q2UsEwfURcpAL9S/Jessica_Ruppert_Headshot.png" alt="Jessica Ruppert, Registered Psychotherapist" className="w-full h-auto object-cover" data-id="element-212" />
                </div>
                <p className="text-center text-sm text-teal/60 mt-3 font-medium" data-id="element-213">
                  Jessica Ruppert, MA, RP (Qualifying)
                </p>
              </div>
            </div>

            <div className="bg-neutral-soft p-8 rounded-2xl mb-12 border-l-4 border-sage" data-id="element-214">
              <h3 className="text-xl font-semibold text-teal mb-4" data-id="element-215">
                My Background
              </h3>
              <p className="text-teal/80 mb-4" data-id="element-216">
                I'm a Registered Psychotherapist (Qualifying) with a Master's
                degree in Counselling Psychology.
              </p>
              <p className="text-teal/80" data-id="element-217">
                But beyond the credentials, here's what really matters: I
                believe in meeting you where you are. No judgement. No pressure
                to be "fixed." Just a safe space to explore what you're feeling,
                figure out what you want, and build from there.
              </p>
            </div>

            <h3 className="text-2xl font-medium text-teal mb-6" data-id="element-218">How I Work</h3>
            <p className="text-teal/80 mb-6" data-id="element-219">
              I draw from evidence-based approaches including CBT, DBT,
              mindfulness-based therapy, and narrative therapy. But I don't
              believe in one-size-fits-all. Some people love structured
              exercises and worksheets. Others need to talk things through and
              process. We'll find what works for you.
            </p>
            <p className="text-teal/80 mb-12" data-id="element-220">
              My approach is rooted in acceptance and empowerment. We start by
              accepting where you are right now, your anxiety, your uncertainty,
              your messy reality, and build from there. Because as Carl Rogers
              said, "The curious paradox is that when I accept myself just as I
              am, then I change."
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16" data-id="element-221">
              <div data-id="element-222">
                <h3 className="text-2xl font-medium text-teal mb-6" data-id="element-223">
                  Who I Work With
                </h3>
                <p className="text-teal/80 mb-4" data-id="element-224">
                  I specialise in working with young adults (typically 20s-30s)
                  who are:
                </p>
                <ul className="space-y-3" data-id="element-225">
                  {['Navigating anxiety and OCD', 'Dealing with life transitions', 'Breaking free from people-pleasing', 'Struggling with comparison culture', 'Building authentic confidence'].map((item, i) => <li key={i} className="flex items-start text-teal/80" data-id="element-226">
                      <CheckCircle2 className="w-5 h-5 text-sage mr-3 flex-shrink-0 mt-0.5" data-id="element-227" />
                      {item}
                    </li>)}
                </ul>
              </div>

              <div className="bg-sage/10 p-8 rounded-2xl" data-id="element-228">
                <h3 className="text-xl font-medium text-teal mb-4" data-id="element-229">
                  Ideal Client?
                </h3>
                <p className="text-teal/80 italic" data-id="element-230">
                  You might be my ideal client if you're tired of performing
                  your life for an invisible audience, exhausted from comparing
                  yourself to everyone else's highlight reel, or just
                  overwhelmed by the pressure to have it all figured out.
                </p>
              </div>
            </div>

            <h3 className="text-2xl font-medium text-teal mb-6" data-id="element-231">
              What You Can Expect
            </h3>
            <p className="text-teal/80 mb-6" data-id="element-232">
              Therapy with me is collaborative. I'm not here to nod silently
              while you talk (though I'm a great listener). I'm here for real
              conversations about what you're going through, what's working, and
              what's not.
            </p>
            <p className="text-teal/80 mb-4" data-id="element-233">You can expect:</p>
            <ul className="space-y-3 mb-16" data-id="element-234">
              {['A judgement-free space where you can be yourself', 'Evidence-based tools that actually work', 'Real talk, not clinical jargon', 'Support in building a life that feels authentic, not performed'].map((item, i) => <li key={i} className="flex items-start text-teal/80" data-id="element-235">
                  <CheckCircle2 className="w-5 h-5 text-sage mr-3 flex-shrink-0 mt-0.5" data-id="element-236" />
                  {item}
                </li>)}
            </ul>

            <div className="text-center bg-terracotta/10 p-10 rounded-3xl" data-id="element-237">
              <h3 className="text-2xl font-medium text-teal mb-4" data-id="element-238">
                Ready to Work Together?
              </h3>
              <p className="text-teal/80 mb-8 max-w-2xl mx-auto" data-id="element-239">
                If this resonates with you, let's talk. Book a free 15-minute
                consultation and we'll see if we're a good fit. No pressure, no
                commitment, just a conversation.
              </p>
              <Button size="lg" onClick={() => window.open(BOOKING_URL, '_blank')} data-id="element-240">
                Book Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}